import * as React from 'react'
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DashboardIcon from '@mui/icons-material/Dashboard'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import BusinessIcon from '@mui/icons-material/Business'
import MenuElement from './MenuItem'
import { idCompany, idUser, nameCompany, rolUser } from '../../utils/session'
import CarRentalIcon from '@mui/icons-material/CarRental'
import MinorCrashIcon from '@mui/icons-material/MinorCrash'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import LockIcon from '@mui/icons-material/Lock'
import { Button } from '@mui/material'
import { usercompaniesService } from '../../services'
import Badge from '@mui/material/Badge'

const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
})

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
})

const closedMixinXs = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `0px`,
})

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
        '& .MuiListItemText-root': {
            display: 'none',
        },
        '& .MuiListItem-root': {
            paddingLeft: '8px !important',
        },
        '& .MuiListItemIcon-root': {
            minWidth: '26px',
        },
    }),
    [theme.breakpoints.down('sm')]: {
        ...(!open && {
            ...closedMixinXs(theme),
            '& .MuiDrawer-paper': closedMixinXs(theme),
            '& .MuiListItemText-root': {
                display: 'none',
            },
        }),
    },
}))

const UsersIconWithBadge = ({ pendingCount }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <Badge
            badgeContent={pendingCount}
            color="error"
            //showZero
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    minWidth: '16px',
                    height: '16px',
                    top: -2,
                    right: -2,
                },
            }}
        >
            <PeopleAltIcon sx={{ color: '#83c63b' }} />
        </Badge>
    </div>
)


export default function Menu(props) {
    const theme = useTheme()
    const [open, setOpen] = React.useState(props.open)
    const [pendingCount, setPendingCount] = React.useState(0)
    const isAdmin = rolUser() === 'COMPANY'
    const navigate = useNavigate()

    const t = props.t

    React.useEffect(() => {
        setOpen(props.open)

        const companyId = idCompany()
        usercompaniesService.getPendingCount(companyId, (count) => {
            setPendingCount(count)
        })
        
    }, [props.open])

    const closeSession = function () {
        localStorage.clear()
        navigate('/login')
    }

    return (
        <DrawerMobify variant="permanent" open={open}>
            <DrawerHeader
                style={{
                    justifyContent: 'flex-start',
                    width: '100%',
                }}
            >
                <IconButton
                    onClick={props.handleDrawerClose}
                    style={{ background: '#83c63b', color: 'white' }}
                >
                    {theme.direction === 'rtl' ? (
                        <ChevronRightIcon />
                    ) : (
                        <ChevronLeftIcon />
                    )}
                </IconButton>
            </DrawerHeader>

            <img
                src={'/icono.png'}
                style={{ width: '100px', marginTop: '30px' }}
            />

            <p
                style={{
                    fontWeight: 'bold',
                    lineHeight: '0px',
                }}
            >
                ¡Bienvenido/a!
            </p>
            <p
                style={{
                    fontWeight: 'bold',
                    lineHeight: '0px',
                    marginBottom: '50px',
                }}
            >
                {nameCompany()}
            </p>

            {isAdmin && (
                <>
                    <MenuElement
                        url={'/dashboard'}
                        icon={<DashboardIcon />}
                        text={t('menu.dashboard')}
                    />
                    <MenuElement
                        url={'/company/edit/' + idCompany()}
                        icon={<BusinessIcon />}
                        text={t('menu.company')}
                    />
                    <MenuElement
                        url={'/timetable/'}
                        icon={<CalendarMonthIcon />}
                        text={t('menu.timetable')}
                    />
                    <MenuElement
                        url={'/users'}
                        icon={<UsersIconWithBadge pendingCount={pendingCount} />}
                        text={t('menu.users')}
                    />
                    <MenuElement
                        url={'/share'}
                        icon={<MinorCrashIcon />}
                        text={t('menu.share')}
                    />
                    <MenuElement
                        url={'/rent'}
                        icon={<CarRentalIcon />}
                        text={t('menu.rent')}
                    />
                    <MenuElement
                        url={'/bill'}
                        icon={<ReceiptLongIcon />}
                        text={t('menu.bill')}
                    />

                    <MenuElement
                        url={'/myaccount/' + idUser()}
                        icon={<ManageAccountsIcon />}
                        text={t('general.myprofile')}
                    />
                    <MenuElement
                        url={'/changepass'}
                        icon={<LockIcon />}
                        text={t('general.changepass')}
                    />
                </>
            )}

            <Button onClick={closeSession} style={{ marginTop: '50px' }}>
                {t('general.closession')}
            </Button>
        </DrawerMobify>
    )
}

const DrawerMobify = styled(Drawer)`
    > div {
        align-items: center;
    }
`
